import React from 'react'
import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { DialogActions } from '@e3dc-react/shell/Elements/Dialog/Dialog'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Stack, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { DatePicker, TextField } from 'src/Libs/inputs/react-hook-form'
import moment from 'moment'
import { formatISO } from '@e3dc-react/shell/Libs/date'
import { OWNER_CHANGE_MIN_DATE } from 'src/Libs/installation-details/const'
import { useUpdateAccountLinkStartAt } from 'src/Libs/installation-details/hooks'
import { ValidationMessageKeys } from 'src/Configuration/validation/translation-keys'

export interface EditAccessStartAtModalProps {
  onClose?: () => void
  startAt: Date
  installationId: number
  accountLinkId: number
  refetchData: () => void
}

const REASON_MIN_LENGTH = 3
const REASON_MAX_LENGTH = 1000

interface FormValues {
  startAt: Date
  reason: string
}

const useStyles = makeStyles()(() => ({
  dialogContent: {
    paddingBottom: 0,
  },
}))

/**
 * Modal to edit the date at which an owner/company was granted access to an installation.
 */
const EditAccessStartAtModal: React.FC<EditAccessStartAtModalProps> = ({ onClose, startAt, installationId, accountLinkId, refetchData }) => {
  const { t } = useCustomTranslation()
  const { enqueueSuccessStack, enqueueErrorStack } = useSnackbar()
  const { classes } = useStyles()
  const today = moment()

  const schema = z.object({
    startAt: z.date().min(new Date(OWNER_CHANGE_MIN_DATE), t(ValidationMessageKeys.minDate)).max(today.toDate(), t(ValidationMessageKeys.maxDate)),
    reason: z.string().trim().min(REASON_MIN_LENGTH).max(REASON_MAX_LENGTH),
  })

  const methods = useForm<FormValues>({
    defaultValues: { startAt, reason: '' },
    mode: 'onChange',
    resolver: zodResolver(schema),
  })

  const {
    handleSubmit,
    formState: { isValid },
  } = methods

  const onUpdateStartAtError = (): void => {
    enqueueErrorStack(t('installation.details.info.edit-access-start-at.error-message'))
  }

  const { mutate: updateAccountLinkStartAt } = useUpdateAccountLinkStartAt({
    onSuccess: () => {
      enqueueSuccessStack(t('installation.details.info.edit-access-start-at.success-message'))
      refetchData()
      onClose?.()
    },
    onError: () => onUpdateStartAtError(),
  })

  const onSubmit = (values: FormValues): void => {
    updateAccountLinkStartAt({
      installationId,
      accountLinkId,
      startAt: formatISO(values.startAt),
      reason: values.reason,
    })
  }

  const dialogActions: DialogActions = {
    primary: {
      name: t('common.save'),
      onClick: handleSubmit(onSubmit),
      disabled: !isValid,
    },
    secondary: [
      {
        name: t('common.cancel'),
        onClick: () => onClose?.(),
      },
    ],
  }

  return (
    <FormProvider {...methods}>
      <Dialog
        title={t('installation.details.info.edit-access-start-at.title')}
        onClose={onClose}
        actions={dialogActions}
        maxWidth="sm"
        contentClassName={classes.dialogContent}
      >
        <Typography variant="body1">{t('installation.details.info.edit-access-start-at.description')}</Typography>
        <Stack mt={4} gap={4}>
          <DatePicker
            name={'startAt'}
            label={t('installation.details.info.edit-owner-start-at.field-label')}
            onTransform={date => date?.toDate() ?? null}
            minDate={moment(OWNER_CHANGE_MIN_DATE)}
            maxDate={today}
          />
          <TextField
            name={'reason'}
            label={t('installation.details.info.edit-owner-start-at.field-reason')}
            multiline
            withTrim
            required
            minLength={REASON_MIN_LENGTH}
            maxLength={REASON_MAX_LENGTH}
          />
        </Stack>
      </Dialog>
    </FormProvider>
  )
}

export default EditAccessStartAtModal
